import { postClientLog } from '@/apis/systemAPI'
import store from '@/store'
import { IClientLog } from '@/utils/types/systemTypes'
import { AxiosError } from 'axios'
import { version } from '../../package.json'
import { LOCAL_STORAGE_ITEM } from './constants'
import { getMobileOS } from './deviceHelper'

/**
 * Gets the current queue from localStorage
 */
function getQueue(): IClientLog[] {
  try {
    const queue = localStorage.getItem(LOCAL_STORAGE_ITEM.CLIENT_LOG_QUEUE)
    return queue ? JSON.parse(queue) : []
  } catch (e) {
    return []
  }
}

/**
 * Saves the queue to localStorage
 */
function saveQueue(queue: IClientLog[]) {
  try {
    localStorage.setItem(LOCAL_STORAGE_ITEM.CLIENT_LOG_QUEUE, JSON.stringify(queue))
  } catch (e) {
    console.error('Failed to save log queue to localStorage', e)
  }
}

/**
 * Attempts to send all queued logs
 */
export async function sendQueuedLogs() {
  const queue = getQueue()
  if (queue.length === 0) return

  const remainingQueue: IClientLog[] = []

  // Try sending logs one by one
  for (const log of queue) {
    try {
      await postClientLog(log)
    } catch (e) {
      const error = e as AxiosError
      // If it fails with permission error, don't keep it in the queue
      if (error?.response?.status === 401 || error?.response?.status === 403) {
        continue
      }
      // If it fails again (e.g. still offline), keep it in the queue for next time
      remainingQueue.push(log)
    }
  }

  saveQueue(remainingQueue)
}

export function addToQueue(log: IClientLog) {
  const queue = getQueue()
  queue.push(log)
  saveQueue(queue)
}

function buildLogPayload(
  message: string,
  stackTrace: string | undefined,
  config: AxiosError['config'] | undefined,
  response: AxiosError['response'] | undefined,
  manualStatusCode: string | undefined,
  manualErrorDetail: string | undefined,
  additionalInfo: Record<string, unknown>
): IClientLog {
  const configWithMetadata = config as AxiosError['config'] & { metadata?: { duration?: number } }

  return {
    message: message,
    level: 'Error',
    stackTrace: stackTrace,
    endpoint: config?.url ? `${config.baseURL || ''}${config.url}` : undefined,
    path: config?.url,
    method: config?.method?.toUpperCase(),
    manufacturingOrderNumber: store.getters.barcode?.manufacturingPlan?.orderNumber,
    machineCode: localStorage.getItem(LOCAL_STORAGE_ITEM.WORK_CENTER_CODE) || store.getters.workCenterCode || '',
    requestPayload: typeof config?.data === 'string' ? config.data : JSON.stringify(config?.data),
    queryString: config?.params ? JSON.stringify(config.params) : undefined,
    contentType: String(config?.headers?.['Content-Type'] || config?.headers?.['content-type'] || ''),
    errorDetail: manualErrorDetail || message || '',
    statusCode: manualStatusCode || response?.status?.toString() || '',
    elapsed: configWithMetadata?.metadata?.duration?.toString(),
    additionalInfo: {
      browser: navigator.userAgent,
      os: getMobileOS(),
      appVersion: version,
      page: window.location.pathname,
      ...additionalInfo,
    },
  }
}

export function queueError(
  error: AxiosError | Error | string,
  additionalInfo: Record<string, unknown> = {},
  manualStatusCode?: string,
  manualErrorDetail?: string
) {
  try {
    let config: AxiosError['config'] | undefined
    let response: AxiosError['response'] | undefined
    let message = 'Unknown error'
    let stackTrace: string | undefined

    if (typeof error === 'string') {
      message = error
    } else {
      config = (error as AxiosError).config
      response = (error as AxiosError).response
      message = error.message || 'Unknown error'
      stackTrace = error.stack
    }

    if (config?.url?.includes('/system/client-log')) {
      return
    }

    const logPayload = buildLogPayload(
      message,
      stackTrace,
      config,
      response,
      manualStatusCode,
      manualErrorDetail,
      additionalInfo
    )

    addToQueue(logPayload)
  } catch (e) {
    console.error('Failed to queue error:', e)
  }
}

// Add listener for network status changes
if (typeof window !== 'undefined') {
  window.addEventListener('online', sendQueuedLogs)
  setInterval(sendQueuedLogs, 5000)
}

/**
 * Logs an error to the system/client-log endpoint.
 * @param error The error object (typically from an Axios response interceptor)
 * @param additionalInfo Optional extra information to include in the log
 * @param manualStatusCode Optional manual status code (e.g. from error handler)
 * @param manualErrorDetail Optional manual error message (e.g. translated text)
 */
export async function logError(
  error: AxiosError | Error | string,
  additionalInfo: Record<string, unknown> = {},
  manualStatusCode?: string,
  manualErrorDetail?: string
) {
  try {
    let config: AxiosError['config'] | undefined
    let response: AxiosError['response'] | undefined
    let message = 'Unknown error'
    let stackTrace: string | undefined

    if (typeof error === 'string') {
      message = error
    } else {
      config = (error as AxiosError).config
      response = (error as AxiosError).response
      message = error.message || 'Unknown error'
      stackTrace = error.stack
    }

    // Avoid infinite loop if logging itself fails
    if (config?.url?.includes('/system/client-log')) {
      return
    }

    const logPayload = buildLogPayload(
      message,
      stackTrace,
      config,
      response,
      manualStatusCode,
      manualErrorDetail,
      additionalInfo
    )

    if (navigator.onLine) {
      try {
        await postClientLog(logPayload)
      } catch (e) {
        const err = e as AxiosError
        // If it fails due to lack of permission, do nothing
        if (err?.response?.status === 401 || err?.response?.status === 403) {
          return
        }
        // If request failed (e.g. timeout or server down), queue it
        addToQueue(logPayload)
      }
    } else {
      // Offline, queue it directly
      addToQueue(logPayload)
    }
  } catch (e) {
    // We don't want to throw an error while trying to log an error
    console.error('Failed to log error to server:', e)
  }
}
